import '../modals.css';

import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { teamsState } from '../../../recoil/atoms/teamsState';
import { tenantSettingsState } from '../../../recoil/atoms/tenantSettingsState';
import { useSetSelectedPlayer } from '../../../recoil/hooks/useSetSelectedPlayer';
import { playerTeamDataSelector } from '../../../recoil/selectors/playerTeamDataSelector';
import { useSetNavigationProps } from '../../../recoil/hooks/useSetNavigationProps';
import { navigationPropsState } from '../../../recoil/atoms/navigationPropsState';
import { useOpenGlobalModal } from '../../../recoil/hooks/useOpenGlobalModal';
import { playersState } from '../../../recoil/atoms/playersState';
import { useAuthContext } from '../../../../common/contexts/useAuthContext';

import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

import { addPlayerToAcademy, addPlayerWithoutData, removePlayerFromAcademy, removePlayerFromOwnTeam } from '../../../services/firestore/players';

import { translate } from '../../../../common/language/translations';
import { DropDown } from '../../controls/dropDowns/DropDown';
import { IconButton } from '../../controls/buttons/IconButton';
import { TextButton } from '../../controls/buttons/TextButton';
import { stringArraysContainSameElements } from '../../../utils/commonUtils';
import { updatePlayerTeamStatus } from '../../../services/firestore/teams';
import { PlayerId } from '../../../types/player';
import { PlatformPosition } from '../../../types/position';
import { getFormationConfig, getPositionsOfFormationConfig } from '../../../utils/formationUtils';


interface PlayerViewModalProps {
  closeModal: (feedback?: boolean) => void;
  playerId: PlayerId;
  playerName: string;
  showPlayerNameInTitle?: boolean;
  onlyConsiderAcademyTeams?: boolean;
}

export const PlayerViewModal: React.FC<PlayerViewModalProps> = ({
  closeModal,
  playerId,
  playerName,
  showPlayerNameInTitle,
  onlyConsiderAcademyTeams,
}) => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);
  const tenantSettings = useRecoilValue(tenantSettingsState);

  const navigationProps = useRecoilValue(navigationPropsState);
  const updateNavigationProps = useSetNavigationProps();
  const { openConfirmModal } = useOpenGlobalModal();
  const { updateSelectedPlayer } = useSetSelectedPlayer();

  const [dropDownExpanded, setDropDownExpanded] = useState<string | undefined>();

  const teams = useRecoilValue(teamsState);
  const playerTeamData = useRecoilValue(playerTeamDataSelector);
  const players = useRecoilValue(playersState);

  const [initialTeamPositionPairList] = useState<{ teamId: string | undefined, position: PlatformPosition | undefined }[]>(() => {
    if (!teams) return [];

    const teamData = playerTeamData && playerId in playerTeamData ? playerTeamData[playerId] : {};

    let teamIds = Object.keys(teamData);
    if (onlyConsiderAcademyTeams) {
      teamIds = teamIds.filter(teamId => teams[teamId].isAcademyTeam);
    }

    const sortedTeamIds = teamIds.sort((a, b) => {
      const teamA = teams[a];
      const teamB = teams[b];
      if (teamA.isAcademyTeam === teamB.isAcademyTeam) {
        return teamA.orderIndex - teamB.orderIndex;
      }
      return teamA.isAcademyTeam ? 1 : -1;
    });

    return sortedTeamIds.map(teamId => ({
      teamId: teamId,
      position: teamData[teamId],
    }));
  });


  const [teamPositionPairList, setTeamPositionPairList] = useState(
    initialTeamPositionPairList.length === 0 ? [{ teamId: undefined, position: undefined }] : initialTeamPositionPairList.map(item => ({ ...item }))
  );


  const getPositionOptionsForTeam = (teamId: string | undefined): string[] => {
    if (!teamId || !teams || !(teamId in teams)) return [];
    const formationConfig = getFormationConfig(teams[teamId].formation, tenantSettings?.formationConfigs);
    return getPositionsOfFormationConfig(formationConfig);
  };


  const handleSelectTeam = (teamId: string[], index: number) => {
    const newTeamPositionPairList = [...teamPositionPairList];

    const newTeamId = teamId.length > 0 ? teamId[0] : undefined;
    newTeamPositionPairList[index].teamId = newTeamId;

    // if a position is already selected for the team, and the position is not valid for the new team, reset the position
    if (newTeamId && newTeamPositionPairList[index].position && !getPositionOptionsForTeam(teamId[0]).includes(newTeamPositionPairList[index].position)) {
      newTeamPositionPairList[index].position = undefined;
    }

    setTeamPositionPairList(newTeamPositionPairList);
  };


  const handleSelectPosition = (position: PlatformPosition[], index: number) => {
    const newTeamPositionPairList = [...teamPositionPairList];
    newTeamPositionPairList[index].position = position.length > 0 ? position[0] : undefined;
    setTeamPositionPairList(newTeamPositionPairList);
  };


  const onConfirmClick = async () => {
    if (!teams || !userConfig || !currentUser) return;

    const playerWasInOwnTeam = initialTeamPositionPairList.some(pair => pair.teamId === 'ownTeam');
    const playerIsInOwnTeam = teamPositionPairList.some(pair => pair.teamId === 'ownTeam');

    const playerWasInAnyAcademyTeam = initialTeamPositionPairList.some(pair => pair.teamId && teams[pair.teamId].isAcademyTeam);
    const playerIsInAnyAcademyTeam = teamPositionPairList.some(pair => pair.teamId && teams[pair.teamId].isAcademyTeam);

    let newPlayerId: PlayerId | undefined = playerId;
    if (playerId === 'newPlayerWithoutData') {
      newPlayerId = await addPlayerWithoutData(playerName, playerIsInAnyAcademyTeam, userConfig.club, currentUser);
    }

    if (newPlayerId === undefined) return;

    const playerHasData = !isNaN(Number(newPlayerId));

    updatePlayerTeamStatus(
      newPlayerId,
      playerName,
      initialTeamPositionPairList as { teamId: string, position: PlatformPosition }[],
      teamPositionPairList as { teamId: string, position: PlatformPosition }[],
      teams,
      userConfig.club,
      userConfig.email,
      currentUser,
    );


    // when adding/removing players to/from ownTeam and academyTeams, we may want to open a confirm-modal
    // (1) if the player is no longer in any academyTeam, we want ask the user if they want to remove the player from the academy
    //     - the player may be added/removed to/from ownTeam in the same action, but clauses should not be relevant for academy players
    // (2) if the player is added/removed to/from ownTeam and is/was not an academy player, we want to ask the user if they want to add a clause
    if (playerWasInAnyAcademyTeam && !playerIsInAnyAcademyTeam) {
      setTimeout(() => {
        openConfirmModal(
          () => removePlayerFromAcademy(newPlayerId, playerIsInOwnTeam, userConfig.club, currentUser),
          'removePlayerFromAcademy?',
          'removePlayerFromAcademy?Info',
        );
      }, 250);
    }

    else if (playerWasInOwnTeam && !playerIsInOwnTeam && !playerWasInAnyAcademyTeam && !playerIsInAnyAcademyTeam && playerHasData) {
      handleOpenAddClauseModal(false);
    }

    else if (!playerWasInOwnTeam && playerIsInOwnTeam && !playerWasInAnyAcademyTeam && !playerIsInAnyAcademyTeam && playerHasData) {
      handleOpenAddClauseModal(true);
    }

    // we want to update the player document when removing the player from ownTeam or academyTeams, regardless of which modal is opened
    if (playerWasInOwnTeam && !playerIsInOwnTeam) {
      removePlayerFromOwnTeam(newPlayerId, players?.[newPlayerId]?.isAcademyPlayer ?? false, userConfig.club, currentUser);
    }
    if (!playerWasInAnyAcademyTeam && playerIsInAnyAcademyTeam) {
      addPlayerToAcademy(newPlayerId, playerName, userConfig.club, currentUser);
    }

    if (playerId === 'newPlayerWithoutData') {
      updateSelectedPlayer({ id: newPlayerId, name: playerName });
    }
    closeModal(true);
  };


  // will only be called if player has data and activeNavigationTab is not economy
  const confirmAddClause = (wasAdded: boolean) => {
    updateNavigationProps({ activeNavigationTab: 'economy', selectedPlayerIdForNewClause: Number(playerId), activeEconomySubTab: wasAdded ? 1 : 0 }, true);
    updateSelectedPlayer(undefined);
  };


  const handleOpenAddClauseModal = (wasAdded: boolean) => {
    if (navigationProps.activeNavigationTab === 'economy' || !userConfig?.accessConfig.hasAccessToEconomy) return;

    setTimeout(() => {
      openConfirmModal(
        () => confirmAddClause(wasAdded),
        'addClause?',
        wasAdded ? 'addClauseForBoughtPlayerInfo' : 'addClauseForSoldPlayerInfo',
      );
    }, 250);
  };


  const shouldShowRemoveIcon = initialTeamPositionPairList.length !== 0 || teamPositionPairList.length > 1;

  const isButtonDisabled = useMemo(() => {

    // this ensures valid selection
    if (teamPositionPairList.some(entry => !entry.teamId || !entry.position)) return true;

    // this ensures that the selection is different from the initial selection
    const initialTeamPositionPairListKeys = initialTeamPositionPairList.map(entry => entry.teamId as string + entry.position as string);
    const teamPositionPairListKeys = teamPositionPairList.map(entry => entry.teamId as string + entry.position as string);
    if (stringArraysContainSameElements(initialTeamPositionPairListKeys, teamPositionPairListKeys)) return true;

    return false;
  }, [initialTeamPositionPairList, teamPositionPairList]);


  const disabledTeams = useMemo(() => {
    const result = [];

    if (!userConfig?.accessConfig.hasAccessToModifyOwnTeam) {
      result.push('ownTeam');
    }

    teamPositionPairList.forEach(entry => {
      if (entry.teamId) {
        result.push(entry.teamId);
      }
    });

    return result;
  }, [userConfig, teamPositionPairList]);


  const teamOptions: string[] = useMemo(() => {
    if (!teams) return [];
    return onlyConsiderAcademyTeams
      ? Object.keys(teams).filter(teamId => teams[teamId].isAcademyTeam)
      : Object.keys(teams);
  }, [teams, onlyConsiderAcademyTeams]);


  // if every team has the same formation, we already know the position options
  const positionOptions: string[] | undefined = useMemo(() => {
    if (!teams || teamOptions.length === 0) return undefined;
    const firstTeamFormation = teams[teamOptions[0]].formation;
    const allTeamsHaveSameFormation = teamOptions.every(teamId => teams[teamId].formation === firstTeamFormation);
    if (allTeamsHaveSameFormation) {
      return getPositionOptionsForTeam(teamOptions[0]);
    }
  }, [teams, teamOptions]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div className='modal-root-container player-view-modal-root'>

      <IconButton
        icon={<CloseIcon style={{ fontSize: 24 }} />}
        onClick={() => closeModal()}
        size={30}
        style={{ position: 'absolute', top: 12, right: 12 }}
        startWhite={true}
      />

      {dropDownExpanded && <div className='modal-empty-background' onClick={() => setDropDownExpanded(undefined)} />}

      <div className='modal-root-title'>
        {showPlayerNameInTitle ? playerName : translate(initialTeamPositionPairList.length === 0 ? 'addToTeam' : 'editTeamStatus', userConfig?.language)}
      </div>

      <div className='modal-divider modal-root-title-divider' />

      <div
        className='modal-content-container'
        style={{ top: 75, bottom: 150 }}
      >
        <div className='modal-content-section'>

          {/* {initialTeamPositionPairList.length > 0 && teamPositionPairList.length === 0 && (
            <div style={{ width: '90%', textAlign: 'center' }}>
              {translate('playerWillBeRemovedFromAllTeams', userConfig?.language)}
            </div>
          )} */}

          {teamPositionPairList.map((entry, index) => {
            return (
              <div
                key={index}
                className='player-view-modal-team-row'
                style={{ top: index * 52 }}
              >
                {shouldShowRemoveIcon && (
                  <IconButton
                    icon={<PersonRemoveIcon style={{ fontSize: 22 }} />}
                    onClick={() => setTeamPositionPairList(teamPositionPairList.filter((_, i) => i !== index))}
                    size={30}
                    startWhite={entry.teamId !== 'ownTeam' || userConfig?.accessConfig.hasAccessToModifyOwnTeam}
                    title={translate('removePlayerFromThisTeam', userConfig?.language)}
                    disabled={entry.teamId === 'ownTeam' && !userConfig?.accessConfig.hasAccessToModifyOwnTeam}
                  />
                )}

                <div style={{ width: 220 }}>
                  <DropDown
                    id={'player-view-modal-team-select-' + index}
                    dropDownOptions={teamOptions}
                    selectedOptions={entry.teamId ? [entry.teamId] : []}
                    setSelectedOptions={value => handleSelectTeam(value, index)}
                    isDropDownExpanded={dropDownExpanded === 'team' + index}
                    setIsDropDownExpanded={value => setDropDownExpanded(value ? 'team' + index : undefined)}
                    defaultDropDownText={'selectTeam'}
                    defaultDropDownTextColor={'#000000'}
                    marginBetweenOptions={0}
                    maxHeight={400 - index * 52 + 'px'}
                    disabledOptions={disabledTeams.filter(teamId => teamId !== entry.teamId)}
                    dropDownTextIfNoAvailableOptions={onlyConsiderAcademyTeams ? translate('youDoNotHaveAnyAcademyTeams', userConfig?.language) : undefined}
                    dropDownDisabled={entry.teamId === 'ownTeam' && !userConfig?.accessConfig.hasAccessToModifyOwnTeam}
                  />
                </div>

                <div style={{ width: shouldShowRemoveIcon ? 170 : 220 }}>
                  <DropDown
                    id={'player-view-modal-position-select-' + index}
                    dropDownOptions={positionOptions ?? getPositionOptionsForTeam(entry.teamId)}
                    selectedOptions={entry.position ? [entry.position] : []}
                    setSelectedOptions={value => handleSelectPosition(value, index)}
                    isDropDownExpanded={dropDownExpanded === 'position' + index}
                    setIsDropDownExpanded={value => setDropDownExpanded(value ? 'position' + index : undefined)}
                    defaultDropDownText={'selectPosition'}
                    defaultDropDownTextColor={'#000000'}
                    marginBetweenOptions={0}
                    maxHeight={400 - index * 52 + 'px'}
                    dropDownTextIfNoAvailableOptions={translate('youMustSelectATeamFirst', userConfig?.language)}
                    dropDownDisabled={entry.teamId === 'ownTeam' && !userConfig?.accessConfig.hasAccessToModifyOwnTeam}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className='player-view-modal-team-link'
        onClick={() => setTeamPositionPairList([...teamPositionPairList, { teamId: undefined, position: undefined }])}
      >
        <PersonAddAlt1Icon style={{ fontSize: 18, marginBottom: -1 }} />
        {translate('addToMoreTeams', userConfig?.language)}
      </div>

      <div className='modal-divider' style={{ position: 'absolute', bottom: 90 }} />

      <TextButton
        onClick={onConfirmClick}
        text={translate('confirm', userConfig?.language)}
        icon={<CheckIcon style={{ fontSize: 22 }} />}
        disabled={isButtonDisabled}
        style={{ position: 'absolute', bottom: 30 }}
      />

    </div>
  );
};
